import { useState, useEffect, useRef, useCallback } from 'react'
import { sendVoiceCommand, transcribeAudio, checkWhisperAvailability, VoiceCommandResponse } from '@/services/voiceService'
import styles from './VoiceCommandModal.module.css'

// Declare SpeechRecognition types for TypeScript
interface SpeechRecognitionEvent extends Event {
    results: SpeechRecognitionResultList
    resultIndex: number
}

interface SpeechRecognitionResultList {
    length: number
    item(index: number): SpeechRecognitionResult
    [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
    length: number
    item(index: number): SpeechRecognitionAlternative
    [index: number]: SpeechRecognitionAlternative
    isFinal: boolean
}

interface SpeechRecognitionAlternative {
    transcript: string
    confidence: number
}

interface SpeechRecognition extends EventTarget {
    continuous: boolean
    interimResults: boolean
    lang: string
    start(): void
    stop(): void
    abort(): void
    onresult: ((event: SpeechRecognitionEvent) => void) | null
    onerror: ((event: Event) => void) | null
    onend: (() => void) | null
    onstart: (() => void) | null
}

declare global {
    interface Window {
        SpeechRecognition: new () => SpeechRecognition
        webkitSpeechRecognition: new () => SpeechRecognition
    }
}

interface VoiceCommandModalProps {
    isOpen: boolean
    onClose: () => void
}

type ModalState = 'idle' | 'listening' | 'processing' | 'success' | 'error'

const MicIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.micIcon}>
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
)

const StopIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={styles.stopIcon}>
        <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
)

const CloseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.closeIcon}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
)

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.checkIcon}>
        <polyline points="20 6 9 17 4 12" />
    </svg>
)

const ErrorIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.errorIcon}>
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
)

export default function VoiceCommandModal({ isOpen, onClose }: VoiceCommandModalProps) {
    const [state, setState] = useState<ModalState>('idle')
    const [transcript, setTranscript] = useState('')
    const [interimTranscript, setInterimTranscript] = useState('')
    const [result, setResult] = useState<VoiceCommandResponse | null>(null)
    const [errorMessage, setErrorMessage] = useState('')
    const [useWhisper, setUseWhisper] = useState(false)
    const [isCheckingWhisper, setIsCheckingWhisper] = useState(true)

    const recognitionRef = useRef<SpeechRecognition | null>(null)
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const audioChunksRef = useRef<Blob[]>([])
    const stateRef = useRef<ModalState>('idle') // Track state for callbacks
    const isProcessingRef = useRef(false) // Prevent rapid button clicks

    // Check Whisper availability on mount
    useEffect(() => {
        const checkWhisper = async () => {
            try {
                const status = await checkWhisperAvailability()
                setUseWhisper(status.available)
                console.log(`Voice recognition service: ${status.service}`)
            } catch {
                setUseWhisper(false)
            } finally {
                setIsCheckingWhisper(false)
            }
        }
        checkWhisper()
    }, [])

    // Keep stateRef in sync with state
    useEffect(() => {
        stateRef.current = state
        // Reset processing flag when state changes to idle, success, or error
        if (state === 'idle' || state === 'success' || state === 'error') {
            isProcessingRef.current = false
        }
    }, [state])

    // Check for browser support
    const isSpeechRecognitionSupported = useCallback(() => {
        return 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window
    }, [])

    // Initialize MediaRecorder for Whisper
    const initMediaRecorder = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 16000,
                }
            })

            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg'
            })

            audioChunksRef.current = []

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data)
                }
            }

            mediaRecorder.onstop = async () => {
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop())

                if (audioChunksRef.current.length === 0) {
                    setErrorMessage('Ses kaydedilemedi.')
                    setState('error')
                    return
                }

                setState('processing')
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })

                try {
                    // Send to Whisper for transcription
                    const transcriptionResult = await transcribeAudio(audioBlob)

                    if (transcriptionResult.success && transcriptionResult.text) {
                        setTranscript(transcriptionResult.text)

                        // Process the command
                        const commandResult = await sendVoiceCommand(transcriptionResult.text)
                        setResult(commandResult)
                        setState(commandResult.success ? 'success' : 'error')
                        if (!commandResult.success) {
                            setErrorMessage(commandResult.message)
                        }
                    } else {
                        setErrorMessage(transcriptionResult.error || 'Ses çevrilemedi.')
                        setState('error')
                    }
                } catch (error: any) {
                    console.error('Whisper transcription error:', error)
                    setErrorMessage(error.message || 'Ses işlenirken hata oluştu.')
                    setState('error')
                }
            }

            return mediaRecorder
        } catch (error: any) {
            console.error('MediaRecorder init error:', error)
            if (error.name === 'NotAllowedError') {
                setErrorMessage('Mikrofon erişimi reddedildi. Lütfen izin verin.')
            } else {
                setErrorMessage('Mikrofon başlatılamadı.')
            }
            setState('error')
            return null
        }
    }, [])

    // Initialize speech recognition
    const initRecognition = useCallback(() => {
        if (!isSpeechRecognitionSupported()) {
            setErrorMessage('Bu tarayıcı ses tanıma desteklemiyor. Lütfen Chrome veya Edge kullanın.')
            return null
        }

        const SpeechRecognitionApi = window.SpeechRecognition || window.webkitSpeechRecognition
        const recognition = new SpeechRecognitionApi()

        recognition.continuous = true
        recognition.interimResults = true
        recognition.lang = 'tr-TR'

        recognition.onstart = () => {
            setState('listening')
            setTranscript('')
            setInterimTranscript('')
            setResult(null)
            setErrorMessage('')
        }

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            let interim = ''
            let final = ''

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i]
                if (result.isFinal) {
                    final += result[0].transcript
                } else {
                    interim += result[0].transcript
                }
            }

            if (final) {
                setTranscript(prev => prev + final)
            }
            setInterimTranscript(interim)
        }

        recognition.onerror = (event: any) => {
            console.error('Speech recognition error:', event.error)
            if (event.error === 'no-speech') {
                setErrorMessage('Ses algılanamadı. Lütfen tekrar deneyin.')
            } else if (event.error === 'not-allowed') {
                setErrorMessage('Mikrofon erişimi reddedildi. Lütfen izin verin.')
            } else {
                setErrorMessage(`Ses tanıma hatası: ${event.error}`)
            }
            setState('error')
        }

        recognition.onend = () => {
            // Use stateRef to get current state (avoids stale closure)
            if (stateRef.current === 'listening') {
                // Recognition ended naturally while still in listening state
                // Could auto-process here if desired
            }
        }

        return recognition
    }, [isSpeechRecognitionSupported])

    // Start listening
    const startListening = useCallback(async () => {
        // Prevent rapid clicks
        if (isProcessingRef.current) return
        isProcessingRef.current = true

        // Reset state
        setTranscript('')
        setInterimTranscript('')
        setResult(null)
        setErrorMessage('')

        if (useWhisper) {
            // Use MediaRecorder + Whisper
            const recorder = await initMediaRecorder()
            if (recorder) {
                mediaRecorderRef.current = recorder
                recorder.start(100) // Collect data every 100ms
                setState('listening')
            } else {
                isProcessingRef.current = false
            }
        } else {
            // Use Web Speech API
            if (!recognitionRef.current) {
                recognitionRef.current = initRecognition()
            }

            if (recognitionRef.current) {
                try {
                    recognitionRef.current.start()
                } catch (err) {
                    console.error('Error starting recognition:', err)
                    setErrorMessage('Ses tanıma başlatılamadı.')
                    setState('error')
                }
            } else {
                isProcessingRef.current = false
            }
        }
    }, [initRecognition, initMediaRecorder, useWhisper])

    // Stop listening and process
    const stopListening = useCallback(async () => {
        // Prevent double-processing
        if (stateRef.current !== 'listening') return

        if (useWhisper) {
            // Stop MediaRecorder - onstop handler will process the audio
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop()
                mediaRecorderRef.current = null
            }
        } else {
            // Stop Web Speech API
            if (recognitionRef.current) {
                recognitionRef.current.stop()
            }

            const finalText = transcript + interimTranscript
            if (!finalText.trim()) {
                setErrorMessage('Boş komut gönderilemez.')
                setState('error')
                return
            }

            setState('processing')
            setTranscript(finalText)
            setInterimTranscript('')

            // Timeout helper for API call (30 seconds)
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => reject(new Error('timeout')), 30000)
            })

            try {
                const response = await Promise.race([
                    sendVoiceCommand(finalText.trim()),
                    timeoutPromise,
                ])
                setResult(response)
                setState(response.success ? 'success' : 'error')
                if (!response.success) {
                    setErrorMessage(response.message)
                }
            } catch (err: any) {
                console.error('Error sending command:', err)
                if (err.message === 'timeout') {
                    setErrorMessage('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.')
                } else {
                    setErrorMessage(err.response?.data?.message || 'Komut gönderilemedi.')
                }
                setState('error')
            }
        }
    }, [transcript, interimTranscript, useWhisper])

    // Reset state
    const reset = useCallback(() => {
        setState('idle')
        setTranscript('')
        setInterimTranscript('')
        setResult(null)
        setErrorMessage('')

        // Clean up media recorder
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop()
        }
        mediaRecorderRef.current = null
        audioChunksRef.current = []
        if (recognitionRef.current) {
            recognitionRef.current.abort()
            recognitionRef.current = null // Clean up ref to allow fresh initialization
        }
    }, [])

    // Cleanup on unmount or close
    useEffect(() => {
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort()
            }
        }
    }, [])

    // Reset when modal opens
    useEffect(() => {
        if (isOpen) {
            reset()
        }
    }, [isOpen, reset])

    if (!isOpen) return null

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose()
        }
    }

    return (
        <div className={styles.backdrop} onClick={handleBackdropClick}>
            <div className={styles.modal}>
                <button className={styles.closeButton} onClick={onClose}>
                    <CloseIcon />
                </button>

                <div className={styles.header}>
                    <h2 className={styles.title}>Sesli Komut</h2>
                    <p className={styles.subtitle}>
                        {state === 'idle' && 'Mikrofona tıklayın ve konuşun'}
                        {state === 'listening' && 'Dinleniyor...'}
                        {state === 'processing' && 'İşleniyor...'}
                        {state === 'success' && 'Başarılı!'}
                        {state === 'error' && 'Hata!'}
                    </p>
                </div>

                <div className={styles.content}>
                    {/* Microphone button */}
                    <div className={styles.micContainer}>
                        {state === 'idle' && (
                            <button className={styles.micButton} onClick={startListening}>
                                <MicIcon />
                            </button>
                        )}
                        {state === 'listening' && (
                            <button className={`${styles.micButton} ${styles.listening}`} onClick={stopListening}>
                                <StopIcon />
                            </button>
                        )}
                        {state === 'processing' && (
                            <div className={`${styles.micButton} ${styles.processing}`}>
                                <div className={styles.spinner} />
                            </div>
                        )}
                        {state === 'success' && (
                            <div className={`${styles.micButton} ${styles.success}`}>
                                <CheckIcon />
                            </div>
                        )}
                        {state === 'error' && (
                            <div className={`${styles.micButton} ${styles.error}`}>
                                <ErrorIcon />
                            </div>
                        )}
                    </div>

                    {/* Transcript display */}
                    <div className={styles.transcriptArea}>
                        {(transcript || interimTranscript) ? (
                            <p className={styles.transcript}>
                                {transcript}
                                <span className={styles.interim}>{interimTranscript}</span>
                            </p>
                        ) : (
                            <p className={styles.placeholder}>
                                {state === 'idle' && '"Ali\'ye görev ata: Rapor hazırla"'}
                                {state === 'listening' && 'Konuşmaya başlayın...'}
                            </p>
                        )}
                    </div>

                    {/* Result or error message */}
                    {result && state === 'success' && (
                        <div className={styles.resultBox}>
                            <p className={styles.resultMessage}>{result.message}</p>
                            {result.action && (
                                <span className={styles.actionBadge}>{result.action}</span>
                            )}
                        </div>
                    )}

                    {errorMessage && state === 'error' && (
                        <div className={styles.errorBox}>
                            <p>{errorMessage}</p>
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    {(state === 'success' || state === 'error') && (
                        <button className={styles.retryButton} onClick={reset}>
                            Tekrar Dene
                        </button>
                    )}
                    {state === 'listening' && (
                        <p className={styles.hint}>Bitirmek için durdur butonuna basın</p>
                    )}
                </div>
            </div>
        </div>
    )
}
